/**
 * Centralized configuration management for the web application.
 * Provides typed access to environment variables with validation and defaults.
 */

interface Config {
    // Environment
    mode: string;
    isDev: boolean;
    isProd: boolean;

    // API Configuration
    api: {
        url: string;
    };

    // Supabase Configuration
    supabase: {
        url: string;
        anonKey: string;
    };

    // Monitoring & Analytics
    sentry: {
        dsn?: string;
        environment: string;
        release?: string;
        sampleRate: number;
        tracesSampleRate: number;
    };

    posthog: {
        key?: string;
        host?: string;
        debug: boolean;
    };
}

/**
 * Validates required environment variables and throws helpful errors
 */
function validateConfig(): void {
    const requiredVars = [
        { key: "VITE_SUPABASE_URL", value: import.meta.env.VITE_SUPABASE_URL },
        { key: "VITE_SUPABASE_ANON_KEY", value: import.meta.env.VITE_SUPABASE_ANON_KEY },
    ];

    const missing = requiredVars.filter(({ value }) => !value);

    if (missing.length > 0) {
        throw new Error(
            `Missing required environment variables: ${missing.map(({ key }) => key).join(", ")}\n` +
                "Please check your .env.local file and ensure all required variables are set."
        );
    }
}

/**
 * Parses a string to a number with fallback
 */
function parseFloat(value: string | undefined, fallback: number): number {
    if (!value) return fallback;
    const parsed = Number.parseFloat(value);
    return Number.isNaN(parsed) ? fallback : parsed;
}

// Validate configuration on module load
validateConfig();

/**
 * Typed configuration object with validation and sensible defaults
 */
export const config: Config = {
    // Environment
    mode: import.meta.env.MODE,
    isDev: import.meta.env.DEV,
    isProd: import.meta.env.PROD,

    // API Configuration
    api: {
        url: import.meta.env.VITE_API_URL || "http://localhost:3001",
    },

    // Supabase Configuration
    supabase: {
        url: import.meta.env.VITE_SUPABASE_URL,
        anonKey: import.meta.env.VITE_SUPABASE_ANON_KEY,
    },

    // Monitoring & Analytics
    sentry: {
        dsn: import.meta.env.VITE_SENTRY_DSN,
        environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,
        release: import.meta.env.VITE_SENTRY_RELEASE,
        sampleRate: parseFloat(import.meta.env.VITE_SENTRY_SAMPLE_RATE, 1.0),
        tracesSampleRate: parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE, 0.1),
    },

    posthog: {
        key: import.meta.env.VITE_PUBLIC_POSTHOG_KEY,
        host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
        debug: import.meta.env.DEV,
    },
} as const;

/**
 * Helper to check if monitoring is enabled
 */
export const isMonitoringEnabled = () => {
    return Boolean(config.sentry.dsn || config.posthog.key);
};

/**
 * Helper to get environment-specific settings
 */
export const getEnvironmentConfig = () => {
    return {
        showDebugInfo: config.isDev,
        enableDetailedLogging: config.isDev,
        apiTimeout: config.isDev ? 30000 : 10000, // 30s dev, 10s prod
    };
};
